import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { uuidv7 } from 'uuidv7';
import { TransactionType } from '../api/transaction-types';
import { useUpdateTransactionTypeApi } from '../api/use-update-transaction-type';
import { transactionDisplayTypeSchema } from '../type/type-types';
import { getAccountIdFromDisplayType, getBucketIdFromDisplayType, getTypeFromDisplayType, } from './use-transaction-form';
// Transaction Bulk Type Schema
const transactionBulkTypeFormSchema = z.object({
    displayType: transactionDisplayTypeSchema,
});
export const useTransactionBulkTypeForm = ({ budgetId, transactionIds, onSuccess }) => {
    const form = useForm({
        resolver: zodResolver(transactionBulkTypeFormSchema),
        defaultValues: {
            displayType: 'inflow',
        },
    });
    // mutations
    const { mutate: updateType, isPending: isUpdatePending } = useUpdateTransactionTypeApi({
        onSuccess,
    });
    /**
     * Handles the form submission event, building the API payload and
     * invoking the update type mutation.
     *
     * @param values - Sanitized transaction bulk type form values.
     */
    const submitForm = useCallback((values) => {
        const payload = toRequestData(budgetId, transactionIds, values);
        updateType(payload);
    }, [updateType, budgetId, transactionIds]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isUpdatePending,
    };
};
/**
 * Build the TransactionTypeRequest payload from form values.
 *
 * @param budgetId - The budget ID
 * @param transactionIds - The transaction IDs including splits
 * @param formValues - The values from the bulk type form
 * @returns A fully populated TransactionTypeRequest for the update operation
 */
function toRequestData(budgetId, transactionIds, formValues) {
    const { displayType } = formValues;
    // Extract the three fields from displayType using the same logic as the transaction form
    const type = getTypeFromDisplayType(displayType);
    const bucketId = getBucketIdFromDisplayType(displayType);
    const transferAccountId = getAccountIdFromDisplayType(displayType) || undefined;
    // Generate transfer transaction IDs for any transactions being converted to transfers
    const transferTransactionIds = {};
    if (type === TransactionType.TRANSFER) {
        transactionIds.forEach((id) => {
            transferTransactionIds[id] = uuidv7();
        });
    }
    return {
        budgetId,
        transactionIds: Array.from(transactionIds),
        type,
        bucketId,
        transferAccountId,
        transferTransactionIds: type === TransactionType.TRANSFER ? transferTransactionIds : undefined,
    };
}
